<script lang="ts" setup>
import { login } from '__generated__/login'

import { computed, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'

import { useClient } from '@base/api/useClient'

import { Background } from '@base/_types/Background'

import BackgroundWrapper from '@base/components/BackgroundWrapper/BackgroundWrapper.vue'
import ImageWrapper from '@base/components/ImageWrapper/ImageWrapper.vue'
import USButton from '@base/components/USButton/USButton.vue'
import USInputText from '@base/components/USInputText/USInputText.vue'

import SplitContent from '@player/_components/SplitContent/SplitContent.vue'

import { accountRouteNames } from '@player/account/router/routeNames'

import signIn from '@player/users/assets/sign-in.png'
import blobBg from '@player/users/assets/sign-in-blob-bg.svg'

import { usersRouteNames } from '@player/users/router/routeNames'

import { LOGIN_QUERY } from '@player/users/api/queries/login'

import { props as propDefinition } from './props'
import { playerTitle } from '@player/_lib/playerTitle'

const bgImage: Background = {
  fullWidth: true,
  overflow: true,
  media: [
    {
      type: 'image',
      position: {
        alignHorizontal: 'left_50',
        alignVertical: 'center',
        centerAnchor: false,
        cover: false,
        foreground: false,
        objectPositionHorizontal: 'center',
        objectPositionVertical: 'center'
      },
      imageSet: {
        mobile: {
          fadeIn: true,
          lazy: true,
          src: blobBg
        }
      }
    }
  ]
}

// Props
const props = defineProps(propDefinition)

// Static Variables
const client = useClient()
const router = useRouter()
const route = useRoute()

useHead({
  title: playerTitle('Log In')
})

// Inputs

// Outputs
const loading = ref<boolean>(false)
const serverError = ref<boolean>(false)
const success = ref<boolean>(false)
const inputError = ref<boolean>(false)
const email = ref<string>('')
const password = ref<string>('')
const loginError = ref<boolean>(false)

const loginMsgError = 'Email or password are incorrect'

const emailError = computed(() => (loginError.value ? loginMsgError : ''))
const passwordError = computed(() => (loginError.value ? loginMsgError : ''))

// Helper Functions
const reset = () => {
  loading.value = false
  serverError.value = false
  success.value = false
  loginError.value = false
  inputError.value = false
}

// Query Functions
const handleSignIn = async () => {
  reset()

  // Prepare Inputs + any Formating

  // Validate Inputs (optional, mainly for validating form inputs)

  // Fetch Data
  try {
    loading.value = true
    const { data } = await client.query<login>({
      query: LOGIN_QUERY,
      variables: { email: email.value, password: password.value },
      fetchPolicy: 'no-cache'
    })

    if (data.login.__typename === 'LoginOutput' && data.login.result) {
      // Successful Login

      if (props.afterLogin) {
        // TODO check if route exists
        router.push(props.afterLogin)
      } else router.push({ name: accountRouteNames.Profile })
    } else if (data.login.__typename === 'LoginErrorOutput') {
      // Login Error
      loginError.value = true
      inputError.value = true
    } else {
      // Server Error
      serverError.value = true
    }
  } catch (e) {
    console.error(e)
    serverError.value = true
  } finally {
    password.value = ''
    loading.value = false
  }
}

// Mutation Functions

// User Interaction Handlers

// Initial Data Fetch

// Fetch Data when the URL changes the props
// watch(props, async () => {
//   await fetchData()
// })

// Fetch data when the view is first rendered
// await fetchData()
</script>

<template>
  <div class="sign-in">
    <BackgroundWrapper :background="bgImage" />
    <SplitContent
      :sticky="true"
      top-offset="var(--player-header-height)"
      class="margin-top--5 margin-bottom--5"
    >
      <template #left>
        <div class="sign-in__form-wrapper">
          <h1 class="heading--h3 margin-bottom--5">Log In</h1>
          <form class="sign-in__form" @submit.prevent="handleSignIn">
            <USInputText
              v-model="email"
              name="email"
              heading="Email"
              class="sign-in__input"
              input-id="email"
              :error="emailError"
              input-type="email"
            />
            <USInputText
              v-model="password"
              name="password"
              heading="Password"
              class="sign-in__input"
              input-id="password"
              :error="passwordError"
              input-type="password"
            />
            <div class="sign-in__buttons margin-top--5">
              <button class="sign-in__submit" type="submit" :disabled="loading">
                <USButton label="Submit" :disabled="loading" />
              </button>
              <router-link :to="{ name: usersRouteNames.ForgotPassword }">
                <USButton
                  label="Forgot password?"
                  :disabled="loading"
                  type="inline"
                />
              </router-link>
            </div>
          </form>
        </div>
      </template>
      <template #right>
        <div class="sign-in__image-right">
          <ImageWrapper :src="signIn" aspect-ratio="1" />
        </div>
      </template>
    </SplitContent>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint' as bp;

.sign-in {
  color: var(--color--neutral-a1);

  &__buttons {
    display: flex;
    flex-flow: row;
    justify-content: space-between;
  }

  &__image-right {
    margin-right: auto;
    margin-left: auto;
    max-width: 370px;
  }

  &__input {
    margin-bottom: var(--spacing--6);
  }
}

:deep(.split-content__content) {
  flex-flow: column-reverse;

  @media (min-width: bp.$breakpoint--tablet) {
    flex-flow: row wrap;
  }
}
</style>
